import { getService } from '@Client/runner.hooks/useService';
import type { Diagram } from 'go';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ShareModal from '../ShareModal/ShareModal';
import IProcessMap, { Step } from '@Shared.Angular/@types/processMap';
import {
  FlowinglyPermissions,
  FormFieldType,
  FormFieldTypeEnum,
  NodeCategory
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import { ProcessMapNodeCategories } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapNodeCategories';
import { DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { DocumentName } from '@Shared.Angular/@types/documentName';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { Guid } from '@Shared.Angular/@types/guid';
import IProcessMapNestDetails from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapNestDetails';
import IProcessMapExclusiveGateway from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapExclusiveGateway';
import DraggableSliderContainer from '@Shared.Angular/flowingly.components/DraggableSlider/DraggableSliderContainer';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { IFlowModelPublishedMessage } from '@Shared.Angular/@types/pubSub';
import IModelerNode from '@Shared.Angular/@types/modelerNode';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import {
  DropDownMenuItem,
  KendoReactDropDownButton
} from '@Shared.Angular/kendo.react/KendoReactDropDownButton';
import IProcessMapNodeViewModel from '@Shared.Angular/@types/processMapNodeViewModel';

type Props = {
  isVisible: boolean;
  processMapId: Guid;
  processMapHeight?: number;
  onProcessMapRetrieved?: (processMap: IProcessMap) => void;
  onProcessMapLoad: (map: IProcessMap) => void;
  onSelectedNodeDataChange: (selectedNode: IProcessMapNodeViewModel) => void;
  isPublicMapView: boolean;
};

const ProcessMapsById: Record<Guid, IProcessMapNestDetails> = {};
const ProcessMapComponentsBySchemaId: Record<Guid, IProcessMapNestDetails> = {};

export const ProcessMap = (props: Props) => {
  const [diagramLoaded, setDiagramLoaded] = useState(false);
  const [diagram, setDiagram] = useState<Diagram>(null);
  const [processMap, setProcessMap] = useState<IProcessMap>(null);
  const [zoomValue, setZoomValue] = useState(100);
  const [currentProcessMapId, setCurrentProcessMapId] = useState<Guid | null>(
    null
  );
  const [initialScale, setInitialScale] = useState<number | null>(null);
  const [flowSchema, setFlowSchema] = useState(null);
  const [isFlowModelEditable, setIsFlowModelEditable] =
    useState<boolean>(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [nodeTagFilter, setNodeTagFilter] = useState<Guid[]>([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showPublishedEventModal, setShowPublishedEventModal] = useState(false);

  const permissionsService = getService('permissionsService');
  const modelerApiService = getService('modelerApiService');
  const pubSubService = getService('pubsubService');
  const flowinglyDiagramService = getService('flowinglyDiagramService');
  const appInsightsService = getService('appInsightsService');
  const goService = getService('goService');
  const documentApiService = getService('documentApiService');
  const runnerPublicMapApiServiceType = getService('runnerPublicMapApiService');
  const appConfig = getService('APP_CONFIG');
  const BPMN_CONSTANTS = getService('BPMN_CONSTANTS');
  const notificationService = getService('notificationService');

  const subscriberId = 'ProcessMapComponent';
  pubSubService.unsubscribeAll(subscriberId);
  pubSubService.subscribe(
    'SIGNALR_WORKFLOW_PUBLISHED',
    (event, message: IFlowModelPublishedMessage) => {
      delete ProcessMapsById[message.flowModelId];
      if (message.flowModelId === currentProcessMapId) {
        setShowPublishedEventModal(true);
      }
    },
    subscriberId
  );

  const diagramContainerDiv = useRef<HTMLDivElement>(null);

  const {
    isVisible,
    processMapId,
    processMapHeight = window.innerHeight - 275,
    onProcessMapLoad,
    onProcessMapRetrieved,
    onSelectedNodeDataChange,
    isPublicMapView
  } = props;

  /**
   * load process map when process map Id changes
   */
  useEffect(() => {
    if (currentProcessMapId !== processMapId) {
      const cachedProcessMap = ProcessMapsById[processMapId];
      if (cachedProcessMap) {
        handleMapLoading(cachedProcessMap as IProcessMap);
      } else {
        loadProcessMapByFlowModelId(processMapId);
      }
    }
  }, [processMapId]);

  /**
   * render diagram when process map loaded
   */
  useEffect(() => {
    if (processMap && processMapId === processMap.flowId) {
      updateViewModelEdit();
      const dia = renderDiagram(processMap);
      setZoomValue(100);
      setDiagram(dia);
      setDiagramLoaded(true);
    }
  }, [processMap, processMapHeight]);

  /**
   * addDiagramListener & removeDiagramListener when diagram changed
   */
  useEffect(() => {
    if (diagram) {
      diagram.addDiagramListener('ChangedSelection', onChangedSelection);
      diagram.addDiagramListener('AnimationFinished', onAnimationFinished);

      return () => {
        diagram.removeDiagramListener('ChangedSelection', onChangedSelection);
        diagram.removeDiagramListener('AnimationFinished', onAnimationFinished);
      };
    }
  }, [diagram]);

  useEffect(() => {
    if (!isVisible) {
      diagram?.clearSelection();
    }
  }, [isVisible]);

  useEffect(() => {
    if (!diagram) {
      return;
    }

    diagram.scale =
      initialScale *
      Math.pow(diagram.commandHandler.zoomFactor, zoomValue - 100);
  }, [zoomValue]);

  useEffect(() => {
    const elem = diagramContainerDiv.current;
    const option = { passive: false };
    elem.addEventListener('wheel', updateZoomFromMouseScroll, option);
    return () => {
      elem.removeEventListener('wheel', updateZoomFromMouseScroll);
      pubSubService.unsubscribeAll(subscriberId);
    };
  }, []);

  useEffect(() => {
    getProcessMapTags();
  }, [flowSchema]);

  const DEADLINETYPES = {
    none: 'None'
  };

  const getProcessMapTags = () => {
    if (!flowSchema?.nodeDataArray) return;

    const processMapTags: Guid[] = flowSchema.nodeDataArray
      .filter((node) => node.tags)
      .flatMap((node) => node.tags);

    setNodeTagFilter(processMapTags);
  };

  const updateViewModelEdit = () => {
    const hasPermission = permissionsService.currentUserHasPermission(
      FlowinglyPermissions.FLOWMODEL_UPDATE
    );

    setIsFlowModelEditable(hasPermission || processMap.editableByCurrentUser);
  };

  const renderDiagram = (map: IProcessMap) => {
    const diagram = flowinglyDiagramService.generateProcessModel(
      {
        flow: map,
        applyAvatar: false,
        modelCustomArgs: {
          scrollMode: goService.Diagram.DocumentScroll
        },
        allowSelect: true,
        dynamicInitialHeight: processMapHeight,
        allowMove: true
      },
      updateZoomFromKeyPress
    );

    if (diagram) {
      diagram.commandHandler.canIncreaseZoom = () => false;
      diagram.commandHandler.canDecreaseZoom = () => false;
      diagram.commandHandler.canResetZoom = () => false;
    }
    return diagram;
  };

  const updateZoomFromKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    setZoomValue((prevZoomValue) => {
      let newValue = prevZoomValue;
      switch (event.key.toUpperCase()) {
        case 'ADD':
          newValue = Math.min(prevZoomValue + 1, 200);
          break;
        case 'SUBTRACT':
          newValue = Math.max(prevZoomValue - 1, 0);
          break;
        case '0':
          newValue = 100;
          break;
        default:
          break;
      }
      return newValue;
    });
  };

  const handleMapLoading = async (map: IProcessMap) => {
    if (onProcessMapRetrieved) {
      onProcessMapRetrieved(map);
    }

    // the event should be published on the initial parent processMap rendering and not on subsequent component renderings.
    if (!currentProcessMapId) {
      pubSubService.publish('PROCESSMAPVIEWV2_LOADED', { name: map.name });
    }

    setCurrentProcessMapId(map.flowId);

    map.Id = map.flowId;
    map.isProcessMap = true;

    const schema = JSON.parse(map.flowSchema ?? '[]');

    setProcessMap(map);
    setFlowSchema(schema);
    onProcessMapLoad(map);

    appInsightsService.trackMetricIfTimerExist('flowRenderProcessMap', {
      flowIdentifier: map.name
    });

    getProcessMapTags();
  };

  const loadProcessMapByFlowModelId = async (flowModelId: Guid) => {
    appInsightsService.startEventTimer('flowRenderProcessMap');
    try {
      const map = isPublicMapView
        ? await runnerPublicMapApiServiceType.getPublicProcessMap(flowModelId)
        : await modelerApiService.getProcessMap(flowModelId, true);

      await handleMapLoading(map as IProcessMap);
      if (map) {
        cacheProcessMap(map);
      }
    } catch (error) {
      await handleMapLoading(null);
    }
  };

  const loadProcessMapBySchemaId = async (schemaId: string) => {
    if (!schemaId) {
      return;
    }
    appInsightsService.startEventTimer('flowRenderProcessMap');
    let map = ProcessMapComponentsBySchemaId[schemaId];
    if (!map) {
      map = await modelerApiService.getProcessMapbySchemaId(
        schemaId,
        true,
        isPublicMapView
      );

      cacheProcessMapComponent(schemaId, map);
    }
    if (map.isPublic || !isPublicMapView) {
      await handleMapLoading(map as IProcessMap);
    }
    return map;
  };

  const onChangedSelection = async () => {
    const selectedNode = diagram.selection.first();

    const selectedNodeData =
      selectedNode instanceof goService.Node ? selectedNode.data : null;

    if (
      selectedNodeData === null ||
      selectedNode.category === NodeCategory.POOL ||
      selectedNode.category === NodeCategory.LANE ||
      selectedNode.category === NodeCategory.EVENT ||
      selectedNode.category === NodeCategory.DIVERGE_GATEWAY ||
      selectedNode.category === NodeCategory.CONVERGE_GATEWAY
    ) {
      onSelectedNodeDataChange(null);
      return;
    }

    let nodeElement = {} as IProcessMapNodeViewModel;

    // If the selected node is an exclusive gateway, fetch from exclusiveGateways
    if (selectedNodeData.category === NodeCategory.EXCLUSIVE_GATEWAY) {
      nodeElement.decisionGateway =
        processMap.exclusiveGateways.find(
          (n) => n.id === selectedNodeData.id
        ) ||
        ({
          name: selectedNodeData.text,
          paths: [],
          decisionField: '',
          decisionStep: ''
        } as IProcessMapExclusiveGateway);
      nodeElement.category = ProcessMapNodeCategories.DecisionGateway;
      onSelectedNodeDataChange(nodeElement);
      return;
    }

    if (selectedNode.category === NodeCategory.COMPONENT) {
      // To close the right panel while switching between components
      onSelectedNodeDataChange(null);
      // To reset the zoom slider
      setZoomValue(100);
      const newMap = await loadProcessMapBySchemaId(
        selectedNodeData.componentSchemaId
      );
      if (!newMap.isPublic && isPublicMapView) {
        notificationService.showWarningToast('This process map is private');
        onSelectedNodeDataChange(null);
        return;
      }
    }

    nodeElement = processMap.steps.find((n) => n.id === selectedNodeData.id);
    const { fields } = nodeElement || {};

    if (!fields || fields.length === 0) {
      const stepFields = await modelerApiService.getProcessMapNodeElement(
        processMap.flowId,
        selectedNodeData.id,
        false,
        isPublicMapView
      );
      if (nodeElement) {
        nodeElement.fields = stepFields;
      }
    }
    if (nodeElement) {
      updateSelectedStepValues(nodeElement);
    }
    onSelectedNodeDataChange(nodeElement);
  };

  const onAnimationFinished = () => {
    setInitialScale(diagram.scale);
    diagram.links.each(function (link) {
      link.invalidateRoute();
    });
  };

  const updateSelectedStepValues = (selectedStep: Step) => {
    selectedStep.deadLine = formatDisplay(
      selectedStep.deadLineNumber,
      selectedStep.deadLineType
    );
    selectedStep.plannedTime = formatDisplay(
      selectedStep.costTimeNumber,
      selectedStep.costTimeType
    );
    selectedStep.waitingTime = formatDisplay(
      selectedStep.waitTime,
      selectedStep.waitTimeType
    );
    selectedStep.stepActor = flowSchema.nodeDataArray.find(
      (n) => n.id == selectedStep.id
    ).actorName;
    selectedStep.reminder = formatDisplay(
      selectedStep.reminderNumber,
      selectedStep.reminderType
    );

    selectedStep.fields?.forEach((f) => {
      f.isNodeFromRunnerProcessMap = true;
      f.type = convertNumericToEnumFieldType(f.type);
    });
  };

  const formatDisplay = (num: number, type: string) => {
    let deadline = null;

    if (type == DEADLINETYPES.none) {
      return DEADLINETYPES.none;
    }

    if (num && type) {
      if (num == 1 && type != DEADLINETYPES.none) {
        type = type.slice(0, type.length - 1);
      }
      deadline = num + ' ' + type;
    }
    return deadline;
  };

  const convertNumericToEnumFieldType = (numfield: number) => {
    switch (numfield) {
      case FormFieldTypeEnum.APPROVAL_COMMENT:
        return FormFieldType.APPROVAL_COMMENT;
      case FormFieldTypeEnum.APPROVAL_RULE:
        return FormFieldType.APPROVAL_RULE;
      case FormFieldTypeEnum.CHECKBOX:
        return FormFieldType.CHECKBOX;
      case FormFieldTypeEnum.CURRENCY:
        return FormFieldType.CURRENCY;
      case FormFieldTypeEnum.EMAIL:
        return FormFieldType.EMAIL;
      case FormFieldTypeEnum.FILE_UPLOAD:
        return FormFieldType.FILE_UPLOAD;
      case FormFieldTypeEnum.INSTRUCTION:
        return FormFieldType.INSTRUCTION;
      case FormFieldTypeEnum.TABLE:
        return FormFieldType.TABLE;
      case FormFieldTypeEnum.TEXT:
        return FormFieldType.TEXT;
      case FormFieldTypeEnum.TEXT_AREA:
        return FormFieldType.TEXTAREA;
      case FormFieldTypeEnum.NUMBER:
        return FormFieldType.NUMBER;
      case FormFieldTypeEnum.PASSWORD:
        return FormFieldType.PASSWORD;
      case FormFieldTypeEnum.RADIO_BUTTON_LIST:
        return FormFieldType.RADIO_BUTTON_LIST;
      case FormFieldTypeEnum.SELECT_LIST:
        return FormFieldType.SELECT_LIST;
      case FormFieldTypeEnum.TASK_LIST:
        return FormFieldType.TASK_LIST;
      case FormFieldTypeEnum.MULTISELECT_LIST:
        return FormFieldType.MULTISELECT_LIST;
      case FormFieldTypeEnum.DATE:
        return FormFieldType.DATE;
      case FormFieldTypeEnum.DATETIME:
        return FormFieldType.DATETIME;
      case FormFieldTypeEnum.SIGNATURE:
        return FormFieldType.SIGNATURE;
      case FormFieldTypeEnum.LOOKUP:
        return FormFieldType.LOOKUP;
      case FormFieldTypeEnum.FORMULA:
        return FormFieldType.FORMULA;
      case FormFieldTypeEnum.ATTACH_DOCUMENT:
        return FormFieldType.ATTACH_DOCUMENT;
      case FormFieldTypeEnum.IMAGE:
        return FormFieldType.IMAGE;
      case FormFieldTypeEnum.PROCEDURE:
        return FormFieldType.PROCEDURE;
      default:
        return numfield;
    }
  };

  const openFlowModelInModeler = () => {
    getService('tempModelerUrlService').openModeler({
      flowModelId: processMapId
    });
  };

  const updateZoomFromMouseScroll = (event: WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      setZoomValue((prevZoomValue) => {
        const scrollDirection = event.deltaY > 0 ? -1 : 1;
        const newZoomValue = Math.max(
          0,
          Math.min(200, prevZoomValue + scrollDirection)
        );
        return newZoomValue;
      });
    }
  };

  const cacheProcessMap = (map: IProcessMapNestDetails) => {
    ProcessMapsById[map.flowId] = map;
  };

  const cacheProcessMapComponent = (schemaId: string, map: IProcessMap) => {
    ProcessMapComponentsBySchemaId[schemaId] = map;
    cacheProcessMap(map);
  };

  const hasComponents = useMemo(() => {
    if (!flowSchema?.nodeDataArray) return false;
    return flowSchema.nodeDataArray.some(
      (node: IModelerNode) => node.category === NodeCategory.COMPONENT
    );
  }, [flowSchema]);

  const printMenuItems: (string | DropDownMenuItem)[] = hasComponents
    ? [
        'Steps',
        'Map',
        {
          text: 'Map & Steps',
          children: [
            {
              text: 'Parent Process Only',
              description: 'Print only the parent process map and steps'
            },
            {
              text: 'Full Details',
              description:
                "Print all parent process and components' maps and steps"
            }
          ]
        }
      ]
    : ['Steps', 'Map', 'Map & Steps'];

  const onPrintItemClick = (event: DropDownButtonItemClickEvent) => {
    const item =
      typeof event.item === 'string'
        ? (event.item as DocumentName)
        : (event.item as DropDownMenuItem).text;
    switch (item as DocumentName) {
      case 'Steps': {
        setShowPrintModal(true);
        documentApiService
          .downloadFlowModelStepsPdf(processMap.flowId)
          .finally(() => setShowPrintModal(false));
        break;
      }
      case 'Map': {
        setShowPrintModal(true);
        documentApiService
          .downloadFlowModelMapPdf(processMap.flowId)
          .finally(() => setShowPrintModal(false));
        break;
      }
      case 'Map & Steps': {
        setShowPrintModal(true);
        documentApiService
          .downloadFlowModelMapAndStepsPdf(processMap.flowId)
          .finally(() => setShowPrintModal(false));
        break;
      }
      case 'Parent Process Only': {
        setShowPrintModal(true);
        documentApiService
          .downloadFlowModelParentMapAndStepsPdf(processMap.flowId)
          .finally(() => setShowPrintModal(false));
        break;
      }
      case 'Full Details': {
        setShowPrintModal(true);
        documentApiService
          .downloadFlowModelMapAndStepsPdf(processMap.flowId)
          .finally(() => setShowPrintModal(false));
        break;
      }
    }
  };

  const highlightNodesWithAnyMatchingTag = (selectedTags: Guid[]) => {
    diagram.startTransaction('tag-filter-color');

    diagram.model.nodeDataArray.forEach((nodeData: IModelerNode) => {
      const hasMatchingTag = nodeData?.tags?.some((tagId) =>
        selectedTags.includes(tagId)
      );
      const node = diagram.findNodeForKey(nodeData.key);

      if (node && nodeData.category === NodeCategory.ACTIVITY) {
        diagram.model.setDataProperty(
          nodeData,
          'color',
          hasMatchingTag
            ? BPMN_CONSTANTS.Theme.FlowinglyHighlightedNode
            : BPMN_CONSTANTS.Theme.FlowinglyWhite
        );
      }
    });

    diagram.commitTransaction('tag-filter-color');
  };

  return (
    <div id="process-map-view">
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      {diagramLoaded && !isPublicMapView && (
        <div id="process-map-view-buttons">
          <div className="process-map-v2-header-actions-left">
            {appConfig.enableBackendDocumentPrinting && (
              <KendoReactDropDownButton
                menuItems={printMenuItems}
                buttonLabel="Print"
                onItemClick={onPrintItemClick}
                type="tertiary"
                classNames="mobile-hide"
              />
            )}
            {isFlowModelEditable && (
              <TextButton
                id="process-map-edit-flow-model-button"
                text="Edit"
                type="secondary"
                onClick={openFlowModelInModeler}
                classNames="mobile-hide"
              />
            )}
            <TextButton
              id="process-map-share-button"
              onClick={() => setShareModalOpen(true)}
              text="Share"
              type="primary"
              iconClass="fa-share-nodes"
            />
          </div>
          <div className="process-map-v2-header-actions-right">
            {appConfig.enableTagging && (
              <TagFilter
                key={currentProcessMapId}
                usedTagIds={nodeTagFilter}
                onApply={highlightNodesWithAnyMatchingTag}
                tagLabel="Filter tags"
              ></TagFilter>
            )}
          </div>
          <ShareModal
            closeModal={() => setShareModalOpen(false)}
            isPublicMap={processMap.isPublic}
            currentProcessMapId={currentProcessMapId}
            open={shareModalOpen}
          />
        </div>
      )}

      {showPrintModal && (
        <Modal
          id="print-progress-modal"
          hideCancelButton
          actionButtonText="Ok"
          onActionClick={() => setShowPrintModal(false)}
          onClose={() => setShowPrintModal(false)}
          size="sm"
        >
          <div className="print-modal-message-text left-align">
            Document generation in progress. The download will be ready shortly!
            You can continue working in the meantime.
          </div>
        </Modal>
      )}

      {showPublishedEventModal && (
        <Modal
          id="published-event-modal"
          actionButtonText="Reload"
          onActionClick={() => {
            setShowPublishedEventModal(false);
            loadProcessMapByFlowModelId(currentProcessMapId);
          }}
          onClose={() => setShowPublishedEventModal(false)}
          onCancelClick={() => setShowPublishedEventModal(false)}
          size="sm"
        >
          <div className="published-event-modal-message-text">
            This process map has been updated. Click 'Reload' to see the latest
            version.
          </div>
        </Modal>
      )}

      <div id={currentProcessMapId} ref={diagramContainerDiv}></div>

      {diagramLoaded && (
        <DraggableSliderContainer
          min={50}
          max={150}
          value={zoomValue}
          onChange={setZoomValue}
        ></DraggableSliderContainer>
      )}
    </div>
  );
};

export default ProcessMap;
